/**
 * @fileoverview Own staff editing of settlement-offer activity and negotiation context. The backend owns saved matter/history records; the parent reconciles timeline changes after explicit writes. Recording an offer is distinct from accepting a settlement, receiving funds, or changing the matter's resolved state.
 */

import React, { useContext, useEffect, useState } from 'react';
import api from '../../../api';
import { AppButton, AppModal, AppTextarea, AppCurrencyInput, AppDropdown, AppCalendar } from '../../shared';
import ToastContext from '../../../ToastContext';



function SettlementTrackerForm({ settlementForm, setSettlementForm, matterNumber, fetchActivities, partyMakingOfferOptions }) {
    const showToast = useContext(ToastContext);

    const [formContent, setFormContent] = useState({
        date: new Date(),
        partyMakingOffer: '',
        amount: null,
        comments: ''
    });

    useEffect(() => {
        if (settlementForm.isEditing && settlementForm.currentSettlement) {
            console.log(settlementForm.currentSettlement);
            setFormContent({
                date: new Date(settlementForm.currentSettlement.Date),
                partyMakingOffer: settlementForm.currentSettlement.PartyMakingOffer,
                amount: settlementForm.currentSettlement.Amount,
                comments: settlementForm.currentSettlement.Details
            });
        }
    }, [settlementForm]);

    const submit =

    () => {
        const payload = {
            ActivityType: 'Settlement',
            Date: formContent.date,
            Details: formContent.comments,
            Amount: formContent.amount,
            PartyMakingOffer: formContent.partyMakingOffer,
            MatterNumber: matterNumber,
            id: settlementForm.currentSettlement.id
        };

        const apiMethod = settlementForm.isEditing ? api.put : api.post;
        const endpoint = settlementForm.isEditing ? `/activity/${settlementForm.currentSettlementId}` : `/activity`;

        apiMethod(endpoint, payload)
            .then(response => {
                fetchActivities();
                showToast({
                    severity: 'success',
                    summary: 'Successful',
                    detail: settlementForm.isEditing ? 'Settlement updated' : 'Tracking info added to activity log'
                });
                setSettlementForm({
                    ...settlementForm,
                    visible: false,
                    isEditing: false,
                    currentSettlement: null
                });
            })
            .catch(error => {
                console.error('Error:', error);
                showToast({ severity: 'error', summary: 'Error', detail: error.message });
            });
    }

    const deleteSettlementOffer =

    () => {
        api.delete('/activity', { data: { id: settlementForm.currentSettlementId } })
            .then(response => {
                if (response.status === 201) {  // Added status check
                    fetchActivities();
                    showToast({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Settlement deleted'
                    });
                    setSettlementForm({
                        ...settlementForm,
                        visible: false,
                        isEditing: false,
                        currentSettlement: null
                    });
                } else {
                    showToast({ severity: 'error', summary: 'Error', detail: 'Unexpected status code' });
                }
            })
            .catch(error => {
                console.error('Error:', error);
                showToast({ severity: 'error', summary: 'Error', detail: error.message });
            });
    }

    const footerContent = (
        <div className="case-details-modal__actions">
            {settlementForm.isEditing && <AppButton label="Delete" icon="pi pi-trash" variant="danger" onClick={deleteSettlementOffer} />}
            <AppButton label={settlementForm.isEditing ? 'Update' : 'Submit'} icon="pi pi-check" onClick={submit} autoFocus />
        </div>
    );

    return (
        <>
            <AppModal
                title={settlementForm.isEditing ? 'Edit Settlement' : 'Track Settlement'}
                description="Capture the offer details, amount, and notes for a settlement event on this matter."
                visible={settlementForm.visible}
                width="min(92vw, 760px)"
                onClose={() => setSettlementForm(prev => ({ ...prev, visible: false }))}
                footer={footerContent}
                className="case-details-modal"
            >
                <div className="case-details-modal__form">
                    <section className="case-details-modal__section">
                        <div className="case-details-modal__section-header">
                            <div>
                                <h3 className="case-details-modal__section-title">Settlement Event</h3>
                                <p className="case-details-modal__section-description">
                                    Record when the offer happened, who made it, and the amount currently on the table.
                                </p>
                            </div>
                        </div>
                        <div className="case-details-modal__section-body">
                            <div className="case-details-modal__grid">
                                <div className="case-details-modal__field">
                                    <label htmlFor="date" className="case-details-modal__label">Date</label>
                                    <AppCalendar
                                        value={formContent.date}
                                        onChange={(e) => setFormContent((prev) => ({ ...prev, date: e.value }))}
                                    />
                                </div>
                                <div className="case-details-modal__field">
                                    <label htmlFor="partyMakingOffer" className="case-details-modal__label">Party Making Offer</label>
                                    <AppDropdown
                                        id="partyMakingOffer"
                                        value={formContent.partyMakingOffer}
                                        options={partyMakingOfferOptions.map((option) => ({ label: option, value: option }))}
                                        onChange={(e) => setFormContent((prev) => ({ ...prev, partyMakingOffer: e.value }))}
                                        placeholder="Select a party"
                                    />
                                </div>
                                <div className="case-details-modal__field">
                                    <label htmlFor="amount" className="case-details-modal__label">Amount</label>
                                    <AppCurrencyInput
                                        id="amount"
                                        value={formContent.amount}
                                        onValueChange={(e) => setFormContent((prev) => ({ ...prev, amount: e.value }))}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="case-details-modal__section">
                        <div className="case-details-modal__section-header">
                            <div>
                                <h3 className="case-details-modal__section-title">Comments</h3>
                                <p className="case-details-modal__section-description">
                                    Add negotiation context or internal notes that should stay attached to this settlement entry.
                                </p>
                            </div>
                        </div>
                        <div className="case-details-modal__section-body">
                            <div className="case-details-modal__field">
                                <label htmlFor="comments" className="case-details-modal__label">Comments</label>
                                <AppTextarea
                                    id="comments"
                                    value={formContent.comments}
                                    onChange={(val) => setFormContent((prev) => ({ ...prev, comments: val }))}
                                    rows={6}
                                />
                            </div>
                        </div>
                    </section>
                </div>
            </AppModal>
        </>
    )

}

export default SettlementTrackerForm;
